import { PageHeader } from '@/components/PageHeader';
import { SortableHeader } from '@/components/SortableHeader';
import { useConfirm } from '@/contexts/ConfirmDialogContext';
import { api } from '@/lib/api';
import { cn, formatDate } from '@/lib/utils';
import {
  Badge,
  Button,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@a7y/tailwind-catalyst';
import type { BlogPost, ContentStatus } from '@a7y/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { ExternalLink } from 'lucide-react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { STATUS_BADGE_COLOR, STATUS_FILTER_OPTIONS } from '../constants';
import { aggregateTagTaxonomy, buildLivePostUrl, buildStagingPreviewUrl } from '../utils';
import { AuthorsTab } from './AuthorsTab';

type Tab = 'posts' | 'authors';

type SortField = 'title' | 'date';

/**
 * The date the list shows for a post: a scheduled post is defined by when it
 * goes live, everything else by its frontmatter publishDate. `iso: null`
 * means there is no real date to show yet.
 */
function displayDate(post: BlogPost): { label: string; iso: string | null } {
  if (post.status === 'scheduled' && post.goLiveAt) {
    return { label: 'Go-live', iso: post.goLiveAt };
  }
  if (post.status === 'published') {
    // publishedAt is stamped server-side at first publication. Posts published
    // before the field existed fall back to their frontmatter date, which for
    // the migrated MDX set is the real publication date.
    return { label: 'Published', iso: post.publishedAt ?? post.publishDate };
  }
  if (post.publishDatePending) {
    // A7Y-4474 D26: a duplicate's `publishDate` is a placeholder (the
    // duplication time) until its first publish stamps the real one —
    // showing that placeholder here would present a fabricated date as real.
    return { label: 'Planned', iso: null };
  }
  // A draft has never gone live, so publishDate is the date it is *aiming* for.
  return { label: 'Planned', iso: post.publishDate };
}

export function BlogListPage() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [actionError, setActionError] = useState<string | null>(null);
  const [previewingId, setPreviewingId] = useState<string | null>(null);
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [tab, setTab] = useState<Tab>('posts');

  const status = searchParams.get('status') ?? 'all';
  const author = searchParams.get('author') ?? 'all';
  const tag = searchParams.get('tag') ?? 'all';

  // Fetch the whole set once and filter in the browser. The API's status filter
  // is itself applied JS-side after an unfiltered Firestore read (no composite
  // index exists for status + createdAt), so scoping the request buys nothing —
  // and refetching per filter would rebuild the author/tag dropdowns from a
  // subset, hiding options the user needs to switch to.
  const listQuery = useQuery({
    queryKey: ['blog-list'],
    queryFn: () => api.blog.list(),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['blog-list'] });

  const unpublishMutation = useMutation({
    mutationFn: (id: string) => api.blog.unpublish(id),
    onSuccess: (res) => {
      if (res.ok) invalidate();
      else setActionError(res.error);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.blog.remove(id),
    onSuccess: (res) => {
      if (res.ok) invalidate();
      else setActionError(res.error);
    },
  });

  const archiveMutation = useMutation({
    mutationFn: (id: string) => api.blog.archive(id),
    onSuccess: (res) => {
      if (res.ok) invalidate();
      else setActionError(res.error);
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: (id: string) => api.blog.duplicate(id),
    onSuccess: (res) => {
      if (res.ok) {
        invalidate();
        // The duplicate is a new draft — open it immediately rather than
        // leaving the editor to hunt for it in the (now-filtered) list.
        navigate(`/admin/content/blog/${res.data.id}`);
      } else {
        setActionError(res.error);
      }
    },
  });

  const handleFilter = (key: 'status' | 'author' | 'tag', value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value === 'all') next.delete(key);
    else next.set(key, value);
    setSearchParams(next, { replace: true });
  };

  const handleUnpublish = async (post: BlogPost) => {
    setActionError(null);
    const ok = await confirm({
      title: 'Unpublish post?',
      description: `"${post.title}" will be removed from the public blog and reverted to draft.`,
      confirmLabel: 'Unpublish',
      confirmColor: 'amber',
    });
    if (ok) unpublishMutation.mutate(post.id);
  };

  const handleDelete = async (post: BlogPost) => {
    setActionError(null);
    const ok = await confirm({
      title: 'Delete post?',
      description: `"${post.title}" and its revision history will be permanently deleted. This cannot be undone.`,
      confirmLabel: 'Delete',
      confirmColor: 'red',
    });
    if (ok) deleteMutation.mutate(post.id);
  };

  const handleArchive = async (post: BlogPost) => {
    setActionError(null);
    const ok = await confirm({
      title: 'Archive post?',
      description: `"${post.title}" will be removed from the public blog and marked archived — its URL then answers 410 Gone. A revision snapshot is written, so it can be restored later.`,
      confirmLabel: 'Archive',
      confirmColor: 'red',
    });
    if (ok) archiveMutation.mutate(post.id);
  };

  const handleDuplicate = (post: BlogPost) => {
    setActionError(null);
    duplicateMutation.mutate(post.id);
  };

  const handleStagingPreview = async (post: BlogPost) => {
    setActionError(null);
    setPreviewingId(post.id);
    try {
      const res = await api.blog.previewToken(post.id);
      if (res.ok) {
        window.open(
          buildStagingPreviewUrl(post.id, res.data.token),
          '_blank',
          'noopener,noreferrer'
        );
      } else {
        setActionError(res.error);
      }
    } finally {
      setPreviewingId(null);
    }
  };

  const allPosts = useMemo(() => listQuery.data?.posts ?? [], [listQuery.data]);

  // Dropdown options come from the full set, so a selection never removes the
  // option you would switch to next.
  const authorOptions = useMemo(
    () =>
      [...new Set(allPosts.map((p) => p.author?.trim()).filter((a): a is string => !!a))].sort(
        (a, b) => a.localeCompare(b)
      ),
    [allPosts]
  );
  const tagOptions = useMemo(() => aggregateTagTaxonomy(allPosts), [allPosts]);

  const posts = useMemo(() => {
    const matched = allPosts.filter(
      (p) =>
        (status === 'all' || p.status === status) &&
        (author === 'all' || p.author?.trim() === author) &&
        (tag === 'all' || (p.tags ?? []).some((t) => t.trim() === tag))
    );
    const direction = sortOrder === 'asc' ? 1 : -1;
    return [...matched].sort((a, b) => {
      if (sortField === 'title') {
        return direction * a.title.localeCompare(b.title);
      }
      // Sort on the same value the Date column renders, so the order always
      // matches what the reader sees.
      // A pending-date duplicate sorts as if it had no date yet (0 —
      // earliest), since there's no real value to compare against.
      const dateOf = (p: BlogPost) => {
        const iso = displayDate(p).iso;
        return iso ? Date.parse(iso) : 0;
      };
      return direction * (dateOf(a) - dateOf(b));
    });
  }, [allPosts, status, author, tag, sortField, sortOrder]);

  const filtered = posts.length !== allPosts.length;

  const handleSort = (field: SortField) => {
    if (field === sortField) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder(field === 'title' ? 'asc' : 'desc');
    }
  };

  return (
    <div className="px-4 py-0">
      <PageHeader title="Blog" subtitle="Author, schedule, and publish blog posts">
        <Button color="blue" onClick={() => navigate('/admin/content/blog/new')}>
          New post
        </Button>
      </PageHeader>

      <div className="mb-6 flex gap-1 border-b border-zinc-200 dark:border-zinc-700">
        {(['posts', 'authors'] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={cn(
              'px-3 py-2 text-sm font-medium capitalize',
              tab === t
                ? 'border-b-2 border-blue-600 text-blue-600 dark:text-blue-400'
                : 'text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200'
            )}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'authors' ? (
        <AuthorsTab />
      ) : (
        <>
          <div className="mb-6 flex flex-wrap items-end gap-4">
            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400">Status</span>
              <Select
                className="w-40"
                value={status}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  handleFilter('status', e.target.value)
                }
              >
                {STATUS_FILTER_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Select>
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400">Author</span>
              <Select
                className="w-48"
                value={author}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  handleFilter('author', e.target.value)
                }
              >
                <option value="all">All authors</option>
                {authorOptions.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
              </Select>
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400">Tag</span>
              <Select
                className="w-48"
                value={tag}
                onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                  handleFilter('tag', e.target.value)
                }
              >
                <option value="all">All tags</option>
                {tagOptions.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </Select>
            </label>

            {filtered && (
              <div className="flex items-center gap-3 pb-2 text-sm text-zinc-500 dark:text-zinc-400">
                <span>
                  {posts.length} of {allPosts.length}
                </span>
                <button
                  onClick={() => setSearchParams(new URLSearchParams(), { replace: true })}
                  className="font-medium text-blue-600 hover:underline dark:text-blue-400"
                >
                  Clear
                </button>
              </div>
            )}
          </div>

          {actionError && (
            <div className="mb-3 rounded-lg bg-red-50 p-3 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-400">
              {actionError}
            </div>
          )}
          {listQuery.isError && (
            <div className="mb-3 rounded-lg bg-red-50 p-3 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-400">
              {listQuery.error?.message || 'Failed to load posts'}
            </div>
          )}

          <Table dense striped>
            <TableHead>
              <TableRow>
                <TableHeader>Status</TableHeader>
                <SortableHeader
                  field="title"
                  label="Title"
                  sortField={sortField}
                  sortOrder={sortOrder}
                  onSort={handleSort}
                />
                <TableHeader>Author</TableHeader>
                <SortableHeader
                  field="date"
                  label="Date"
                  sortField={sortField}
                  sortOrder={sortOrder}
                  onSort={handleSort}
                />
                <TableHeader>Tags</TableHeader>
                <TableHeader className="text-right">Actions</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {listQuery.isLoading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center">
                    <div className="mx-auto h-6 w-6 animate-spin rounded-full border-4 border-gray-300 border-t-blue-600 dark:border-zinc-600" />
                  </TableCell>
                </TableRow>
              ) : posts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="py-8 text-center">
                    <p className="text-zinc-500">No posts yet</p>
                    <p className="mt-1 text-sm text-zinc-400">
                      Create one with the "New post" button.
                    </p>
                  </TableCell>
                </TableRow>
              ) : (
                posts.map((post) => {
                  const isPublished = post.status === 'published';
                  const isArchived = post.status === 'archived';
                  return (
                    <TableRow key={post.id}>
                      <TableCell>
                        <Badge color={STATUS_BADGE_COLOR[post.status as ContentStatus] ?? 'zinc'}>
                          {post.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-md align-top font-medium whitespace-normal">
                        <Link
                          to={`/admin/content/blog/${post.id}`}
                          className="text-blue-600 hover:underline dark:text-blue-400"
                        >
                          {post.title}
                        </Link>
                        {isPublished && (
                          <a
                            href={buildLivePostUrl(post.slug)}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-0.5 flex w-fit items-center gap-1 text-xs font-normal text-zinc-500 hover:text-zinc-700 hover:underline dark:text-zinc-400 dark:hover:text-zinc-200"
                          >
                            View live
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                      </TableCell>
                      <TableCell className="text-zinc-600 dark:text-zinc-400">
                        {post.author}
                      </TableCell>
                      <TableCell className="align-top text-zinc-500">
                        {(() => {
                          const { label, iso } = displayDate(post);
                          return (
                            <>
                              <div className="text-[11px] tracking-wide text-zinc-400 uppercase">
                                {label}
                              </div>
                              <div>{iso ? formatDate(iso) : 'Set at publish'}</div>
                            </>
                          );
                        })()}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {post.tags.slice(0, 3).map((tag) => (
                            <Badge key={tag} color="zinc">
                              {tag}
                            </Badge>
                          ))}
                          {post.tags.length > 3 && (
                            <span className="text-xs text-zinc-400">+{post.tags.length - 3}</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button plain onClick={() => navigate(`/admin/content/blog/${post.id}`)}>
                            Edit
                          </Button>
                          <Button
                            outline
                            disabled={isPublished || isArchived || previewingId === post.id}
                            title={
                              isPublished || isArchived
                                ? 'Staging preview serves draft/scheduled posts only — open the post to use the in-admin preview.'
                                : undefined
                            }
                            onClick={() => handleStagingPreview(post)}
                          >
                            {previewingId === post.id ? 'Opening…' : 'Preview'}
                          </Button>
                          {isPublished && (
                            <Button
                              outline
                              disabled={unpublishMutation.isPending}
                              onClick={() => handleUnpublish(post)}
                            >
                              Unpublish
                            </Button>
                          )}
                          <Button
                            outline
                            disabled={duplicateMutation.isPending}
                            onClick={() => handleDuplicate(post)}
                          >
                            Duplicate
                          </Button>
                          {!isArchived && (
                            <Button
                              plain
                              disabled={archiveMutation.isPending}
                              onClick={() => handleArchive(post)}
                            >
                              Archive
                            </Button>
                          )}
                          <Button
                            plain
                            disabled={deleteMutation.isPending}
                            onClick={() => handleDelete(post)}
                          >
                            Delete
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </>
      )}
    </div>
  );
}
